var Column	= require("./Column"),
	Text	= require("./type/Text"),
	Row		= require("../data/Row"),
	Cell	= require("../data/Cell");

/**
 * ColumnConfiguration represents the configuration of all columns within
 * one Sheet. The ColumnConfiguration handles the mapping of object
 * properties to certain columns,, labeling, type definition and also
 * display information like width and alignment.
 *
 * @param {Object} oConfig -  An configuration object that contains all necessary column definitions
 * @param {Object} oConfig.columns -  An array of Column definitions. {@see Column} for further details
 * @param {string} [oConfig.hierarchyLevel] -  Name of the property that holds the hierarchy level
 * @param {boolean} [oConfig.hideHeader] - Flag that allows to hide the header row
 * @param {SharedStrings} oSharedStrings - SharedStrings instance that will be used by all Cells
 * @param {Styles} oStyles - Style collection that is used to register Cell or row specific styles
 * @module binding/ColumnBinding
 * @constructor
 */
function ColumnConfiguration(oConfig, oSharedStrings, oStyles) {
	var aColumns, oColumn, i;

	this.columns = [];
	this._row = null;
	this.styles = oStyles;
	this.sharedStrings = oSharedStrings;
	this.hideHeader = oConfig.hideHeader;

	Object.defineProperty(this, "hierarchyLevelProperty", { value: oConfig.hierarchyLevel });

	aColumns = oConfig.columns;

	for (i = 0; i < aColumns.length; i++) {

		/* As specified, the hierarchy level indentation will only be applied on the first row */
		if (i == 0) {
			if (typeof aColumns[i] === "string") {
				aColumns[i] = { property: aColumns[i] };
			}

			aColumns[i].hierarchyLevel = this.hierarchyLevelProperty;
		}

		oColumn = aColumns[i] instanceof Column ?
			aColumns[i] : new Column(aColumns[i], this.sharedStrings, this.styles);

		this.columns.push(oColumn);
	}
}

/**
 * Initializes the objects that are necessary for the binding. This
 * components are:
 * - a template Row
 * - a Cell for each Column
 * - a Style for each data type referenced by each Column
 *
 * @private
 */
ColumnConfiguration.prototype._initBinding = function() {
	var i;

	this._row = new Row();

	for (i = 0; i < this.columns.length; i++) {
		this._row.addCell(this.columns[i].getCell());
	}
};

/**
 * Creates an new Row and binds the provided data to the Row Cells according to the current ColumnConfiguration.
 *
 * @param {Object} oData - The data object that has to be bound to the row
 * @param {number} nHierarchyLevel - Hierarchy level that will be used for indent and outline
 * @returns {Row} - Returns the created Row that contains the provided data
 */
ColumnConfiguration.prototype.bind = function(oData, nHierarchyLevel) {
	var i, normalizedHierarchyLevel;

	if (this._row === null) {
		this._initBinding();
	}

	/* Normalise hierarchy depth to not exceed the maximum depth of 7 */
	normalizedHierarchyLevel = Math.min(nHierarchyLevel, 7);

	/*
	 * We need to add, update or delete the outline attribute because
	 * we reuse the row object
	 */
	if (normalizedHierarchyLevel && !this._row.outline) {
		this._row.addAttribute({
			id: "outline",
			name: "outlineLevel",
			value: normalizedHierarchyLevel
		});
	} else if (normalizedHierarchyLevel && this._row.outline) {
		this._row.outline.value = normalizedHierarchyLevel;
	} else if (!normalizedHierarchyLevel && this._row.outline) {
		this._row.remove("outline");
	}

	for (i = 0; i < this.columns.length; i++) {
		this.columns[i].bind(oData);
	}

	return this._row;
};

/**
 * Creates the header Row according to the current ColumnConfiguration and returns it.
 *
 * @returns {Row} - Header row according to the ColumnConfiguration
 */
ColumnConfiguration.prototype.getHeader = function() {
	var cell, row, type;

	if (this.hideHeader) {
		return null;
	}

	row = new Row();
	type = new Text({
		font: {
			name: "Arial",
			size: 11,
			bold: true
		},
		fill: {
			color: "F7F7F7"
		}
	});

	/* Register header style */
	type.registerAt(this.styles);

	row.setStyle(type.getStyleId({}));
	row.addAttribute({
		name: "customFormat",
		value: "1"
	});

	this.columns.forEach(function(column) {

		/* Create new Cell and assign value */
		cell = new Cell(type, this.sharedStrings);
		cell.setValue("label", column);

		row.addCell(cell);
	}.bind(this));

	return row;
};

/**
 *
 * @param sGroupName
 * @return {Row}
 */
ColumnConfiguration.prototype.getGroupHeader = function(sGroupName) {
	var cell, row, type, data;

	row = new Row();
	type = new Text({
		font: {
			name: "Arial",
			size: 11,
			bold: true
		},
		fill: {
			color: "F2F2F2"
		}
	});
	data = {
		name: sGroupName
	};

	/* Register header style */
	type.registerAt(this.styles);

	row.setStyle(type.getStyleId(data));
	row.addAttribute({
		name: "customFormat",
		value: "1"
	});

	row.spans.value = "1:" + this.size();

	/* Create new Cell and assign value */
	cell = new Cell(type, this.sharedStrings);
	cell.setValue("name", data);

	row.addCell(cell);

	return row;
};

/**
 * Returns the size of the ColumnConfiguration - respectively the
 * count of the configured columns.
 *
 * @returns {number} Count of configured columns
 */
ColumnConfiguration.prototype.size = function() {
	return this.columns.length;
};

module.exports = ColumnConfiguration;